import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { getExercisesByMuscle, fetchExercises, localizeExerciseName, type Exercise } from "@/lib/fitnessApi";
import AvatarCard from "@/components/AvatarCard";
import { FULL_BODY_IS_AVATURN_MODEL } from "@/components/avatar/miniMirrorCalibration";
import { useFitnessResource } from "@/hooks/useFitnessResource";
import { readBodyMapRecovery } from "@/lib/fitnessBodyMapRecovery";
import type { MuscleStatus } from "@/lib/fitnessBodyCharge";
import { MUSCLE_GROUP_MAP } from "@/lib/fitnessMuscleGroups";
import FitnessDataState from "@/components/fitness/FitnessDataState";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import { getAnimationUrl } from "@/config/avatarAnimations";

const STATUS_COLORS: Record<MuscleStatus, { bg: string; border: string; selectedBg: string; selectedBorder: string }> = {
  needsRest: {
    bg: "hsl(0 70% 50% / 0.12)",
    border: "1px solid hsl(0 70% 50% / 0.20)",
    selectedBg: "hsl(0 70% 50% / 0.30)",
    selectedBorder: "1.5px solid hsl(0 70% 50% / 0.70)",
  },
  recovering: {
    bg: "hsl(45 80% 50% / 0.10)",
    border: "1px solid hsl(45 80% 50% / 0.18)",
    selectedBg: "hsl(45 80% 50% / 0.28)",
    selectedBorder: "1.5px solid hsl(45 80% 50% / 0.65)",
  },
  ready: {
    bg: "hsl(140 60% 45% / 0.10)",
    border: "1px solid hsl(140 60% 45% / 0.18)",
    selectedBg: "hsl(140 60% 45% / 0.30)",
    selectedBorder: "1.5px solid hsl(140 60% 45% / 0.70)",
  },
  notTrained: {
    bg: "hsl(187 72% 50% / 0.05)",
    border: "1px solid hsl(187 72% 50% / 0.10)",
    selectedBg: "hsl(187 72% 50% / 0.30)",
    selectedBorder: "1.5px solid hsl(187 72% 50% / 0.70)",
  },
};

// Front-view muscle shortcuts only. Posterior groups stay available through
// the visible category controls, rather than covering unrelated front anatomy.
interface MuscleButton { id: string; top: string; left: string; width: string; height: string; mapTo?: string }
const MUSCLE_BUTTONS: readonly MuscleButton[] = [
  { id: "chest", top: "28%", left: "42%", width: "16%", height: "7%" },
  { id: "shoulders", top: "22%", left: "30%", width: "10%", height: "5%" },
  { id: "shoulders_r", top: "22%", left: "60%", width: "10%", height: "5%", mapTo: "shoulders" },
  { id: "biceps", top: "32%", left: "24%", width: "7%", height: "9%" },
  { id: "biceps_r", top: "32%", left: "69%", width: "7%", height: "9%", mapTo: "biceps" },
  { id: "abs", mapTo: "core", top: "36%", left: "42%", width: "16%", height: "10%" },
  { id: "quads", mapTo: "legs", top: "52%", left: "36%", width: "12%", height: "14%" },
  { id: "quads_r", top: "52%", left: "52%", width: "12%", height: "14%", mapTo: "legs" },
  { id: "calves", top: "72%", left: "37%", width: "10%", height: "10%" },
  { id: "calves_r", top: "72%", left: "53%", width: "10%", height: "10%", mapTo: "calves" },
];

// Visible controls keep every catalog category reachable independently of the
// avatar's camera, view or overlapping percentage-based hotspots.
const MUSCLE_CATEGORIES = ["chest", "back", "shoulders", "biceps", "triceps", "legs", "core", "glutes", "calves", "cardio"] as const;

const MUSCLE_LABELS: Record<string, { en: string; pt: string; nl: string }> = {
  chest: { en: "Chest", pt: "Peito", nl: "Borst" },
  shoulders: { en: "Shoulders", pt: "Ombros", nl: "Schouders" },
  biceps: { en: "Biceps", pt: "Bíceps", nl: "Biceps" },
  triceps: { en: "Triceps", pt: "Tríceps", nl: "Triceps" },
  abs: { en: "Abs", pt: "Abdominais", nl: "Buikspieren" },
  quads: { en: "Quads", pt: "Quadríceps", nl: "Quadriceps" },
  hamstrings: { en: "Hamstrings", pt: "Posteriores", nl: "Hamstrings" },
  glutes: { en: "Glutes", pt: "Glúteos", nl: "Bilspieren" },
  calves: { en: "Calves", pt: "Panturrilhas", nl: "Kuiten" },
  back: { en: "Back", pt: "Costas", nl: "Rug" },
};

const FitnessBodyMap: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { avatarUrl, loading: avatarLoading, hasAvatar } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície (bug 25/08). Predicado canônico: quem escolheu avatar sem ter
  // GLB próprio também cai na energia — nunca placeholder quebrado.
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;
  const [selected, setSelected] = useState<string | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loadingEx, setLoadingEx] = useState(false);
  const recovery = useFitnessResource(readBodyMapRecovery);
  const muscleStatuses = recovery.data ?? {};
  const statusLoading = recovery.status !== "ready";

  useEffect(() => {
    if (!selected) { setExercises([]); setLoadingEx(false); return; }
    let current = true;
    setLoadingEx(true);
    const muscle = selected;
    // Try ExerciseDB API first, fall back to seed data
    fetchExercises(muscle, undefined, 8).then(exs => {
      if (!current) return;
      if (exs.length === 0) {
        setExercises(getExercisesByMuscle(muscle));
      } else {
        setExercises(exs);
      }
      setLoadingEx(false);
    }).catch(() => {
      if (!current) return;
      setExercises(getExercisesByMuscle(muscle));
      setLoadingEx(false);
    });
    return () => { current = false; };
  }, [selected]);

  const getMuscleLabel = (id: string) => {
    if (MUSCLE_GROUP_MAP[id]) return t(MUSCLE_GROUP_MAP[id]);
    if (id === "calves") return t("fitness.muscles.calves");
    const labels = MUSCLE_LABELS[id];
    if (!labels) return id;
    return lang === "PT-BR" ? labels.pt : lang === "NL" ? labels.nl : labels.en;
  };

  const handleSelect = (id: string) => {
    const muscle = MUSCLE_BUTTONS.find(m => m.id === id);
    const mappedId = muscle?.mapTo || id;
    setSelected(prev => prev === mappedId ? null : mappedId);
  };

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]" style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 mb-4 active:opacity-70">
        <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("fitness.bodyMap.title")}</p>
        <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.bodyMap.title")}</h1>
      </motion.div>

      <p id="bodymap-muscle-selection" className="text-[11px] font-body text-foreground/50 mb-3">{t("fitness.bodyMap.selectMuscle")}</p>
      <div role="group" aria-labelledby="bodymap-muscle-selection" className="mb-4 flex flex-wrap gap-2">
        {MUSCLE_CATEGORIES.map(id => {
          const isSelected = selected === id;
          const status: MuscleStatus = muscleStatuses[id] || "notTrained";
          const colors = STATUS_COLORS[status];
          return (
            <button key={id} type="button" onClick={() => handleSelect(id)} aria-pressed={isSelected}
              aria-describedby={!statusLoading ? "bodymap-status-" + status : undefined}
              className="min-h-11 rounded-full px-4 py-2 text-xs font-display text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              style={{ background: isSelected ? colors.selectedBg : "hsl(var(--card))", border: isSelected ? colors.selectedBorder : "1px solid hsl(var(--border))" }}>
              {getMuscleLabel(id)}
            </button>
          );
        })}
      </div>

      {/* 3D Avatar with muscle overlay buttons */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl mb-5 relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, hsl(220 40% 6%), hsl(225 35% 4%))", border: "1px solid hsl(187 72% 50% / 0.15)", height: "380px" }}>
        {/* Avatar canvas — pointer-events none so buttons work */}
        <div className="absolute inset-0 pointer-events-none">
          {!avatarLoading && !surfaceLoading && (
            showAvatar ? (
              /* LEGACY-NAMING GUARD: isAvaturnModel stays true — gates
                 calibration profile A in FROZEN AvatarCore. Catalog `.glb`
                 in /prerendered/ are Avaturn-origin geometry. cameraDistance
                 4.5 here (vs 4.7 in MirrorHome/Modules) because of the
                 body-map overlay framing. Do NOT flip false — see hotfix v3. */
              <AvatarCard modelUrl={avatarUrl || '/avatar/model_small.glb'} height="100%" fbxUrl={getAnimationUrl('fitness')} isAvaturnModel={FULL_BODY_IS_AVATURN_MODEL} lookAtYOverride={1.3} cameraDistanceOverride={4.5} />
            ) : (
              <VyvEnergy state="idle" ratio={0.38} interactive={false} />
            )
          )}
        </div>

        {/* Optional spatial shortcuts share the same selection as the visible controls. */}
        {MUSCLE_BUTTONS.map(m => {
          const muscleId = m.mapTo || m.id;
          const isSelected = selected === muscleId;
          const status: MuscleStatus = muscleStatuses[muscleId] || "notTrained";
          const colors = STATUS_COLORS[status];
          return (
            <button key={m.id} type="button" onClick={() => handleSelect(m.id)} aria-label={getMuscleLabel(muscleId) + " — " + t("fitness.bodyMap.title")} aria-pressed={isSelected} aria-describedby={!statusLoading ? "bodymap-status-" + status : undefined}
              className="absolute rounded-lg transition-all duration-300"
              style={{
                top: m.top, left: m.left, width: m.width, height: m.height,
                background: isSelected ? colors.selectedBg : colors.bg,
                border: isSelected ? colors.selectedBorder : colors.border,
                boxShadow: isSelected ? "0 0 12px hsl(187 72% 50% / 0.4)" : "none",
                pointerEvents: "auto",
                zIndex: 10,
              }}
            />
          );
        })}

        {/* Selected label */}
        {selected && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 px-4 py-1.5 rounded-full"
            style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.40)", backdropFilter: "blur(8px)" }}>
            <span className="font-display text-[12px] font-bold" style={{ color: "hsl(187 72% 55%)" }}>{getMuscleLabel(selected)}</span>
          </motion.div>
        )}
      </motion.div>

      <p className="mb-3 text-xs leading-relaxed text-muted-foreground">{t("fitness.bodyMap.recoveryHint")}</p>
      {recovery.status === "error" && <FitnessDataState status="error" busy={recovery.isRefreshing} onRetry={recovery.refresh} />}

      {/* Status legend */}
      {!statusLoading && (
        <div className="flex flex-wrap gap-3 mb-4">
          {([
            { status: "needsRest" as MuscleStatus, labelKey: "fitness.bodyMap.trained24h", color: "hsl(0 70% 50%)" },
            { status: "recovering" as MuscleStatus, labelKey: "fitness.bodyMap.trained1to2d", color: "hsl(45 80% 50%)" },
            { status: "ready" as MuscleStatus, labelKey: "fitness.bodyMap.trained2plusd", color: "hsl(140 60% 45%)" },
            { status: "notTrained" as MuscleStatus, labelKey: "fitness.bodyMap.noRecentRecord", color: "hsl(187 72% 50%)" },
          ]).map(item => (
            <div key={item.status} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color, opacity: 0.7 }} />
              <span id={"bodymap-status-" + item.status} className="text-[10px] font-body text-foreground/50">
                {t(item.labelKey)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Exercise list for selected muscle */}
      <AnimatePresence mode="wait">
        {selected && (
          <motion.div key={selected} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(187 72% 50% / 0.45)" }}>
              {t("fitness.bodyMap.exercises")} — {getMuscleLabel(selected)}
            </p>
            {loadingEx ? (
              <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" /></div>
            ) : exercises.length === 0 ? (
              <p className="text-[12px] font-body text-foreground/40 text-center py-4">{t("fitness.bodyMap.noExercises") || "No exercises found"}</p>
            ) : (
              <div className="space-y-2">
                {exercises.slice(0, 8).map((ex, i) => (
                  <motion.div key={ex.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                    className="rounded-xl p-3 flex items-center gap-3" style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.10)" }}>
                    {ex.gifUrl ? (
                      <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 relative">
                        <img src={ex.gifUrl} className="w-full h-full object-cover" alt="" loading="lazy" />
                        <div className="absolute inset-x-0 bottom-0 h-1/3" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.6))" }} />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center" style={{ background: "hsl(187 72% 50% / 0.08)" }}>
                        <span className="text-[18px]">💪</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-[12px] font-semibold text-foreground truncate">{localizeExerciseName(ex.name, t, lang)}</p>
                      <p className="text-[9px] font-body mt-0.5" style={{ color: "hsl(218 14% 50%)" }}>{ex.equipment} · {ex.target}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

    </MobileLayout>
  );
};

export default FitnessBodyMap;
